import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/context.js';

function formatElapsed(seconds: number): string {
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} m ${seconds % 60} s`;
}

/**
 * A small translucent dragon head that "thinks" while the model prepares an
 * answer: it bobs, blinks, glances around and lets out little smoke rings.
 */
export function ThinkingDragon() {
  const { t } = useI18n();
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <span className="thinking-dragon" role="status" aria-label={t('composer.thinking')}>
      <svg className="thinking-dragon-art" viewBox="0 0 50 36" width="38" height="27" aria-hidden="true">
        <defs>
          <linearGradient id="dragon-glass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.36" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <g className="dragon-head">
          {/* short swept-back horns and back spikes */}
          <path className="dragon-part" d="M14.5 10.5 Q11 7 7 5 Q14 4 18.5 9.5 Z" />
          <path className="dragon-part" d="M22 8.5 Q20 5 16.5 2.5 Q22.5 2 25.5 7.5 Z" />
          <path className="dragon-fin" d="M9.5 17.5 L4.5 15.5 L7.5 20 Z M9 22.5 L4 23 L8.5 25.5 Z" />
          {/* rounded head with a medium snout */}
          <path
            className="dragon-glass"
            d="M8 22 C7 14 13 8 21 8 C26 8 29 10.5 32 13 C35 14 41 15 43.5 18 Q45 20.5 42.5 22.5 C40 25 35 25.5 30 25.5 C27 27.5 22 28.5 18 28.5 C12 28.5 8.5 26 8 22 Z"
          />
          <path className="dragon-fang" d="M35 25.3 L36.2 28 L37.6 25.2 Z" />
          <path className="dragon-mouth" d="M30.5 24.5 Q36 26 41.5 22.5" fill="none" strokeWidth="1.3" strokeLinecap="round" />
          <circle className="dragon-nostril-dot" cx="41" cy="18.3" r="0.9" />
          {/* attentive eye with a soft slit pupil and a determined brow */}
          <g className="dragon-eye">
            <path className="dragon-eye-white" d="M20.5 18 Q24.5 12.5 31 15 Q30.5 20.8 25.5 20.8 Q21.5 20.8 20.5 18 Z" />
            <ellipse className="dragon-pupil" cx="26.3" cy="17.2" rx="1.5" ry="2.7" />
            <circle className="dragon-glint" cx="27" cy="16" r="0.7" />
          </g>
          <path className="dragon-brow" d="M20 13.2 L32 14.8" fill="none" strokeWidth="1.7" strokeLinecap="round" />
        </g>
        <circle className="dragon-smoke dragon-smoke-1" cx="46" cy="16.5" r="1.8" />
        <circle className="dragon-smoke dragon-smoke-2" cx="46" cy="16.5" r="1.3" />
      </svg>
      <span className="thinking-shimmer">{t('composer.thinking')}</span>
      {seconds >= 3 && <span className="thinking-dragon-time">{formatElapsed(seconds)}</span>}
    </span>
  );
}
